import { context, trace, propagation, TextMapPropagator, TraceFlags } from '@opentelemetry/api';

// Helper function to generate traceparent header value
function generateTraceparent(traceId, spanId, traceFlags) {
  return `00-${traceId}-${spanId}-${traceFlags.toString(16).padStart(2, '0')}`;
}

// Custom W3CTextMapPropagator class implementing the TextMapPropagator interface
export  class W3CTextMapPropagator  {
  // Inject trace context into the carrier (headers)
  inject(context, carrier, setter) {
    const span = trace.getSpan(context);
    if (!span) return;

    const spanContext = span.spanContext();
    if (!spanContext || !trace.isSpanContextValid(spanContext)) return;

    // Generate the traceparent header
    const traceparent = generateTraceparent(spanContext.traceId, spanContext.spanId, spanContext.traceFlags);

    // Inject the traceparent into the carrier (e.g., HTTP headers)
    setter.set(carrier, 'traceparent', traceparent);

    // Optionally inject tracestate if available
    if (spanContext.traceState) {
      setter.set(carrier, 'tracestate', spanContext.traceState.serialize());
    }
  }

  // Extract trace context from the carrier (headers)
  extract(context, carrier, getter) {
    const traceparent = getter.get(carrier, 'traceparent');
    if (!traceparent) return context;

    // Split the traceparent value and validate
    const parts = traceparent.split('-');
    if (parts.length !== 4 || parts[0] !== '00') return context;

    const traceId = parts[1];
    const spanId = parts[2];
    const traceFlags = parseInt(parts[3], 16);

    if (!trace.isValidTraceId(traceId) || !trace.isValidSpanId(spanId)) return context;

    let traceState;
    const tracestateHeader = getter.get(carrier, 'tracestate');
    if (tracestateHeader) {
      traceState = propagation.createTraceState(tracestateHeader);
    }

    // Create a new span context from the traceparent and tracestate
    const spanContext = {
      traceId,
      spanId,
      traceFlags: traceFlags & TraceFlags.SAMPLED, // Retain only the sampled flag
      isRemote: true,
      traceState,
    };

    // Set the span context in the current context
    return trace.setSpanContext(context, spanContext);
  }

  // Return the fields that are injected/extracted by the propagator
  fields() {
    return ['traceparent', 'tracestate'];
  }
}